import * as React from 'react';
import getCurrentSelectedTableView from 'mini-mail-list-store/lib/utils/getSelectedTableView';
import listViewStore from 'mini-mail-list-store/lib/store/Store';
import loadInitialMailTable from 'mini-mail-list-actions/lib/actions/tables/loadInitialMailTable';
import MailListPlaceholder from './MailListPlaceholder';
import MailListViewState from 'mini-mail-list-store/lib/store/schema/MailListViewState';
import { isInSearchView } from 'mini-mail-list-store/lib/selectors/isInSearchView';
import { MailListEmptyScreen } from './MailListEmptyScreen';
import { MailListEmptyScreenProps, PreListBlock } from '../schema/MailListEmptyScreen.types';
import { mergeOverrideStyles } from 'mini-styles/lib/utils/mergeOverrideStyles';
import { NetworkErrorScreen } from 'mini-error-handler/lib/components/NetworkErrorScreen';
import { preventModalScroll } from 'prevent-modal-scroll';
import { SearchResultsEmptyScreen } from 'mini-search-zero-results-view';

const styles = require('./MailListEmptyState.scss');
const classNames = require('classnames/bind').bind(styles);

interface MailListEmptyStateProps extends MailListEmptyScreenProps {
    stateType: MailListViewState;
    preListBlocks?: PreListBlock[];
}

const MailListEmptyState = React.forwardRef(
    (props: MailListEmptyStateProps, ref?: React.RefObject<HTMLDivElement>) => {
        const isInSearch = listViewStore.shouldShowSearchBox;
        let emptyStateElement: JSX.Element = null;
        const { folderName, viewFilter, focusedViewFilter, preListBlocks } = props;

        const preListComponents = preListBlocks?.map(preListBlock => preListBlock.component);

        const preListComponentHeight = preListBlocks?.reduce(
            (finalHeight, preListBlock) => finalHeight + preListBlock?.height,
            0 /** Initial values */
        );

        switch (props.stateType) {
            case MailListViewState.Loading:
                emptyStateElement = <MailListPlaceholder />;
                break;

            case MailListViewState.Empty:
                emptyStateElement = isInSearch ? (
                    <SearchResultsEmptyScreen allowRetry={false} />
                ) : (
                    <MailListEmptyScreen
                        folderName={folderName}
                        viewFilter={viewFilter}
                        focusedViewFilter={focusedViewFilter}
                    />
                );
                break;

            case MailListViewState.Blank:
                // Currently only used when user focuses in search box without ever committing a search
                // In this case we show a blank page
                break;

            case MailListViewState.LoadInitialError:
                const currentTableView = getCurrentSelectedTableView();
                emptyStateElement = (
                    <NetworkErrorScreen
                        isFullScreen
                        containerClassName={classNames('emptyListElementContainer')}
                        actionButton={{ onClick: () => loadInitialMailTable(currentTableView) }}
                    />
                );
                break;
        }

        return (
            <div
                className={classNames(styles.mailListEmptyState, styles.virtualList, {
                    [styles.searchListEmptyState]: isInSearchView(),
                })}
                ref={ref}>
                {preListComponents && (
                    <div className={styles.prelistblock}>{preListComponents}</div>
                )}
                <div
                    className={classNames(
                        'emptyListElementContainer',
                        mergeOverrideStyles({
                            top: `${preListComponentHeight}px`,
                            height: `calc(100% - ${preListComponentHeight}px)`,
                        })
                    )}>
                    {emptyStateElement}
                </div>
            </div>
        );
    }
);

export default preventModalScroll(
    MailListEmptyState,
    (component: any): HTMLDivElement => component
);
